import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { HistoricalTrendsService } from './his-trends-util.services';
import { OPEN_ALL_SECTIONS } from '../store/user';
import * as _ from 'lodash';
import { Subscription } from 'rxjs/Subscription';
import { FormGroup, FormControl, Validators, FormBuilder } from '@angular/forms';

@Component({
  selector: 'app-his-trends-utl',
  templateUrl: './his-trends-utl.component.html',
  styleUrls: ['./his-trends-utl.component.css']
})
export class HisTrendsUtlComponent implements OnInit, OnDestroy {
  debug = false;

  // header
  header: string;
  subHeader: string;
  paragraphText: string = '';

  // Used to manage state of full screen in all app
  currentUserUIScreen;

  // Spinner
  searchProgress = false;

  // Error Fetching Data?
  errorFetching = false;

  // Table Data
  tableData;
  tableView;

  overViewTableOpen: boolean;

  datasets = [
    { id: '1', label: 'Total United States' },
    {
      id: '2',
      label: 'Total non-federal short-term general and other special'
    },
    { id: '3', label: 'Total community hospitals' },
    { id: '4', label: 'Non-Government not-for-profit community hospitals' },
    { id: '5', label: 'Investor-owned (for-profit) community hospitals' },
    { id: '6', label: 'State and local government community hospitals' }
  ];

  dataSetSelected = this.datasets[0];
  searchForm: FormGroup;
  formSpecOptions: object;
  yearCols: number;
  tableDataHeaders: Array<string>;
  dbStartYear: string;
  dbEndYear: string;
  viewableRangeStart: string;
  viewableRangeEnd: string;
  filterForFiveYrIncr: boolean;
  tableControlsOpen: boolean = true;

  subscriptions: Subscription[] = [];

  // Download PDF/Excel Component
  downloads: any;

  // use to set value in form for mobile or desktop
  displayingMobile;

  constructor(
    private store: Store<any>,
    private hisTrendsUtlService: HistoricalTrendsService,
    private fb: FormBuilder
  ) {
    this.subscriptions.push(
      store.select('currentUser').subscribe(currentUser => {
        this.currentUserUIScreen = currentUser;
        this.setTableControlsOpen(currentUser);
      })
    );
  }

  ngOnInit() {
    this.overViewTableOpen = true;
    this.yearCols = 4;
    this.dbStartYear = '1946';
    this.dbEndYear = '2022';

    this.filterForFiveYrIncr = false;

    this.formSpecOptions = {
      validation: [
        {
          required: true
        },
        {
          pattern: '^[0-9]{4}$'
        }
      ]
    };

    /** create search form and set to default values **/
    this.searchForm = this.fb.group({
      earliestYear: new FormControl(this.dbStartYear, this.assignValidation(this.formSpecOptions)),
      latestYear: new FormControl(this.dbEndYear, this.assignValidation(this.formSpecOptions))
    });

    this.resetViewableRange();

    /** subscribe to years entry form and call for table data only when valid */
    this.subscriptions.push(
      this.searchForm.statusChanges.subscribe(newStatus => {
        if (newStatus === 'VALID') {
          this.resetViewableRange();
          this.getTableData(
            this.dataSetSelected.id,
            this.searchForm.get('earliestYear').value,
            this.searchForm.get('latestYear').value
          );
        }
      })
    );

    /**
     * Initial Data Grab always uses known database limits for years
     */
    this.getTableData(this.dataSetSelected.id, this.dbStartYear, this.dbEndYear);

    /** check if user open app in mobile to show only 2 years in mobile */
    this.checkIfMobileScreen();
  }

  checkIfMobileScreen() {
    if (document.documentElement.clientWidth < 990) {
      this.filterYearInputForMobile();
    }
  }

  @HostListener('window:resize', ['$event'])
  onReSize(event) {
    if (event.target.innerWidth <= 990 && !this.displayingMobile) {
      this.filterYearInputForMobile();
    }

    if (event.target.innerWidth >= 991 && this.displayingMobile) {
      this.filterYearInputForDesktop();
    }
  }

  filterYearInputForMobile() {
    this.searchForm.get('earliestYear').setValue(2019);
    this.searchForm.get('latestYear').setValue(2022);
    this.displayingMobile = true;
  }

  filterYearInputForDesktop() {
    this.searchForm.get('earliestYear').setValue(1946);
    this.searchForm.get('latestYear').setValue(2022);
    this.displayingMobile = false;
  }

  setTableControlsOpen(currentUser) {
    this.tableControlsOpen = currentUser.tableControlsOpen;
  }

  resetViewableRange() {
    const toCtrl = this.searchForm.get('latestYear').value;
    this.setViewableRange(toCtrl);
  }

  setViewableRange(latest: string) {
    let calculatedRangeEnd;

    if (parseFloat(latest) >= parseFloat(this.dbEndYear)) {
      this.viewableRangeStart = this.dbEndYear;
      calculatedRangeEnd = (parseFloat(this.dbEndYear) - this.yearCols).toString();
    } else {
      this.viewableRangeStart = latest;
      calculatedRangeEnd = (parseFloat(this.viewableRangeStart) - this.yearCols).toString();
    }

    this.viewableRangeEnd = calculatedRangeEnd;
  }

  onChangeSelect(event, data) {
    event.preventDefault();
    this.dataSetSelected = this.datasets[data.id - 1];
    this.getTableData(
      this.dataSetSelected.id,
      this.searchForm.get('earliestYear').value,
      this.searchForm.get('latestYear').value
    );
  }

  assignValidation(formSpecOptions: Object) {
    const myValidators = [];
    if (formSpecOptions.hasOwnProperty('validation')) {
      formSpecOptions['validation'].forEach(valNode => {
        const val = this.setValidator(valNode);
        if (typeof val === 'function') {
          myValidators.push(val);
        }
      });
    } else {
      const val = this.setValidator(formSpecOptions);
      if (typeof val === 'function') {
        myValidators.push(val);
      }
    }
    return myValidators;
  }

  setValidator(valNode: any) {
    if (valNode.required === true) {
      return Validators.required;
    }
    if (valNode.hasOwnProperty('maxLength')) {
      return Validators.maxLength(valNode.maxLength);
    }
    if (valNode.hasOwnProperty('pattern')) {
      let pattern;
      if (valNode.pattern === 'floatRegex') {
        const floatRegex = new RegExp('^[0-9]+.?[0-9]*$');
        pattern = floatRegex;
      } else {
        pattern = valNode.pattern;
      }
      return Validators.pattern(pattern);
    }
  }

  /**
   * MAIN DATA PULL
   * @param {string} dataSetIndx
   * @param {string} earliestYear
   * @param {string} latestYear
   */
  getTableData(dataSetIndx: string, earliestYear: string, latestYear: string) {
    const searchOpts = {
      tableId: 'historical',
      startYear: earliestYear,
      endYear: latestYear,
      class: dataSetIndx
    };
    this.searchProgress = true;
    this.subscriptions.push(
      this.hisTrendsUtlService.getTable1Data(searchOpts).subscribe(
        ({ data }) => {
          let myData = _.cloneDeep(data);
          // Filter Empty Rows from section
          if (myData.getTable1Data.data.section[0].label === 'Total United States') {
            let filteredSection = this.filterEmptyRowsForTotalUS(data);
            myData.getTable1Data.data.section = filteredSection.section;
          }
          this.searchProgress = false;
          const resp = myData.getTable1Data;
          this.downloads = resp.download;
          this.paragraphText = resp.tableDetails
            .replace(/\>\s+\</g, '><')
            .replace(/(\r\n|\n|\r)/gm, '')
            .trim();
          const htmlArray = resp.tableTitle.split(':');
          this.header = htmlArray[0];
          this.subHeader = htmlArray[1];
          this.tableData = _.get(resp, 'data'); // save all data, use filter to change/shift tableView

          this.tableView = this.filterTable(this.tableData);
          this.tableDataHeaders = this.getHeaders(this.tableView);
          this.errorFetching = false;
        },
        error => {
          console.error('error fetching from graphql', error);
          this.searchProgress = false;
          this.errorFetching = true;
        }
      )
    );
  }

  /**
   * filter the entire table to create a tableView
   * @param data
   * @returns {Array}
   */
  filterTable(data) {
    let yearsToRemove = [];
    const filtered = [];

    if (this.filterForFiveYrIncr) {
      const except5YearIncr: Array<string> = this.except5YearIncr(this.dbStartYear, this.dbEndYear);
      yearsToRemove = yearsToRemove.concat(except5YearIncr);
    }
    /** filter to only viewable columns **/
    const exceptViewable: Array<string> = this.exceptYearRange(
      this.dbStartYear,
      this.dbEndYear,
      this.viewableRangeStart,
      this.viewableRangeEnd
    );
    yearsToRemove = yearsToRemove.concat(exceptViewable);

    if (data.hasOwnProperty('section') && data.section.length > 0) {
      data.section.forEach(section => {
        const filtSec = this.filterSection(section, yearsToRemove);
        if (!_.isEmpty(filtSec)) {
          filtered.push(filtSec);
        }
      });
    }

    return filtered;
  }

  filterSection(section, yearsToRemove) {
    const filtSect = {};
    // only the first section has this header
    if (section.row !== null) {
      filtSect['row'] = section.row.map(function(obj) {
        const newHeaderRowObj = {};
        newHeaderRowObj['label'] = obj.label;
        newHeaderRowObj['cell'] = obj.cell.filter(function(cell) {
          return !_.includes(yearsToRemove, cell.year);
        });
        return newHeaderRowObj;
      });
    }
    filtSect['subsection'] = section.subsection.map(function(obj) {
      const newObj = {};
      newObj['label'] = obj.label;
      newObj['row'] = obj.row.map(function(row) {
        const rowObj = {};
        rowObj['label'] = row.label;
        rowObj['cell'] = row.cell.filter(function(cell) {
          return !_.includes(yearsToRemove, cell.year);
        });
        return rowObj;
      });
      return newObj;
    });
    return filtSect;
  }

  filterEmptyRowsForTotalUS(data) {
    return {
      section: data.getTable1Data.data.section
        .filter(s => s.label === 'Total United States')
        .map(i => {
          return {
            label: i.label,
            row: i.row.filter(
              i => i.label !== 'Adjusted Avg. Daily Census' && i.label !== 'Average Stay (Days)'
            ),
            subsection: i.subsection.map(obj => {
              if (obj.label == 'FTE Personnel') {
                return {
                  label: obj.label,
                  row: obj.row.filter(r => r.label !== 'Per 100 Adjusted Census')
                };
              } else if (obj.label == 'Total Expenses') {
                return {
                  label: obj.label,
                  row: obj.row.filter(
                    r =>
                      r.label !== 'Adjusted Per Inpatient Stay' &&
                      r.label !== 'Adjusted Per Inpatient Day'
                  )
                };
              } else {
                return obj;
              }
            })
          };
        })
    };
  }

  /**
   * Returns all years except those in 5 year increments
   * @param {number} startYear
   * @param {number} endYear
   * @returns {Array<string>}
   */
  except5YearIncr(startYear: string, endYear: string): Array<string> {
    const yearsArray = [];
    let i = parseFloat(startYear) + 1; // we want the start year even if it's not a rounded year
    while (i < parseFloat(endYear)) {
      i++;
      const iStr: string = i.toString();
      const lastChar: string = iStr.substr(iStr.length - 1);
      if (lastChar.match(/[1-4,6-9]/)) {
        yearsArray.push(iStr);
      }
    }
    return yearsArray;
  }

  /**
   * Returns all years from a master range except those that fall between startYear and endYear, including startYear and endYear
   * @param {number} earliestYear
   * @param {number} latestYear
   * @param {number} startYear
   * @param {number} endYear
   * @returns {Array<string>}
   */
  exceptYearRange(
    earliestYear: string,
    latestYear: string,
    startYear: string,
    endYear: string
  ): Array<string> {
    const yearsArray = [];
    if (endYear !== earliestYear) {
      yearsArray.push(earliestYear);
    }

    let below = parseFloat(earliestYear);
    while (below < parseFloat(endYear)) {
      below++;
      /* don't exclude end year, we want the whole range */
      if (below !== parseFloat(endYear)) {
        yearsArray.push(below.toString());
      }
    }
    let above = parseFloat(startYear);
    while (above < parseFloat(latestYear)) {
      above++;
      yearsArray.push(above.toString());
    }
    return yearsArray;
  }

  tableHeaderLeft() {
    const fromCtrl = this.searchForm.get('earliestYear').value;
    const toCtrl = this.searchForm.get('latestYear').value;

    if (this.viewableRangeStart >= toCtrl) {
      this.viewableRangeStart = toCtrl;
    } else {
      // this.viewableRangeEnd = this.incrementString(this.viewableRangeEnd, '++');
      this.setViewableRange(this.incrementString(this.viewableRangeStart, '++'));
      this.tableView = this.filterTable(this.tableData);
      this.tableDataHeaders = this.getHeaders(this.tableView);
    }
    return 0;
  }

  tableHeaderRight() {
    const fromCtrl = this.searchForm.get('earliestYear').value;
    const toCtrl = this.searchForm.get('latestYear').value;

    if (this.viewableRangeEnd <= fromCtrl) {
      this.viewableRangeEnd = fromCtrl;
    } else {
      // this.viewableRangeEnd = this.incrementString(this.viewableRangeEnd, '--');
      this.setViewableRange(this.incrementString(this.viewableRangeStart, '--'));
      this.tableView = this.filterTable(this.tableData);
      this.tableDataHeaders = this.getHeaders(this.tableView);
    }
    return 0;
  }

  incrementString(strNumb: string, operator: string) {
    if (!_.isEmpty(strNumb) && !_.isEmpty(operator)) {
      if (operator === '--') {
        let myNum = parseFloat(strNumb);
        myNum--;
        return myNum.toString();
      }
      if (operator === '++') {
        let myNum = parseFloat(strNumb);
        myNum++;
        return myNum.toString();
      }
    }
  }

  expandAll() {
    this.store.dispatch({
      type: OPEN_ALL_SECTIONS,
      payload: {
        allSectionsOpen: true
      }
    });
  }

  collapseAll() {
    this.store.dispatch({
      type: OPEN_ALL_SECTIONS,
      payload: {
        allSectionsOpen: false
      }
    });
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => {
      if (typeof subscription.unsubscribe === 'function') {
        subscription.unsubscribe();
      }
    });
  }

  getHeaders(data) {
    const firstRow = _.get(data, '[0].row[0].cell');
    return _.map(firstRow, 'year');
  }
}



// WEBPACK FOOTER //
// ./src/app/t1-his-trends-utl/his-trends-utl.component.ts